import { enqueueSnackbar } from "notistack";
import { NETWORK } from "../config";
import getChain from "./getChain";

// Codes we raise ourselves. Numeric codes come from the wallet and follow
// EIP-1193 / EIP-1474: https://docs.metamask.io/wallet/reference/json-rpc-errors/
export const NO_PROVIDER = "NO_PROVIDER";
export const NO_ACCOUNTS = "NO_ACCOUNTS";

const MESSAGES = {
  4001: {
    message: "You rejected the connection request in MetaMask.",
    hint: "Click Connect again, then choose Connect in the MetaMask popup.",
  },
  4100: {
    message: "MetaMask has not authorised this site for that account.",
    hint: "Open MetaMask > Connected sites, remove this site, then connect again.",
  },
  4200: {
    message: "Your wallet does not support the method this site needs.",
    hint: "This usually means another wallet extension answered instead of MetaMask.",
  },
  4900: {
    message: "MetaMask is not connected to any network.",
    hint: "Open MetaMask and check that a network is selected.",
  },
  4901: {
    message: "MetaMask is not connected to the required network.",
    hint: "Switch MetaMask to the network shown in the details below.",
  },
  "-32002": {
    message: "MetaMask already has a connection request waiting.",
    hint: "Open the MetaMask extension and approve (or dismiss) the pending request, then try again.",
  },
  "-32601": {
    message: "Your wallet rejected the request as an unknown method.",
    hint: "This usually means another wallet extension answered instead of MetaMask.",
  },
  "-32603": {
    message: "MetaMask reported an internal error.",
    hint: "Try disabling other wallet extensions, or reload the page and connect again.",
  },
  [NO_PROVIDER]: {
    message: "No MetaMask provider was found in this browser.",
    hint: "Install or enable MetaMask. If another wallet extension is installed, disable it and reload.",
  },
  [NO_ACCOUNTS]: {
    message: "MetaMask returned no accounts.",
    hint: "Open MetaMask > Connected sites, connect at least one account to this site, then try again.",
  },
};

const FALLBACK = {
  message: "Could not connect to MetaMask.",
  hint: "Send the details below to the team so we can identify the cause.",
};

export const createWalletError = (code, message) => {
  const error = new Error(message);
  error.code = code;
  return error;
};

// Wallet errors arrive wrapped at different depths depending on whether they
// came straight from the provider or were re-thrown by ethers.
const extractCode = (error) => {
  const candidates = [
    error?.code,
    error?.error?.code,
    error?.data?.code,
    error?.data?.originalError?.code,
    error?.info?.error?.code,
  ];
  return candidates.find((code) => code !== undefined && code !== null);
};

const extractMessage = (error) =>
  error?.error?.message || error?.data?.message || error?.message || String(error);

export const describeWalletError = (error) => {
  const code = extractCode(error);
  const known = code === undefined ? undefined : MESSAGES[String(code)];
  return {
    code: code === undefined ? "UNKNOWN" : code,
    raw: extractMessage(error),
    ...(known || FALLBACK),
  };
};

// Identify every wallet that injected itself, so we can tell whether MetaMask
// is really the extension answering our requests on the affected machine.
const detectInjectedWallets = () => {
  const provider = window.ethereum;
  if (!provider) return [];

  const FLAGS = {
    isMetaMask: "MetaMask",
    isCoinbaseWallet: "Coinbase",
    isBraveWallet: "Brave",
    isRabby: "Rabby",
    isTrust: "Trust",
    isTrustWallet: "Trust",
    isPhantom: "Phantom",
    isOkxWallet: "OKX",
    isExodus: "Exodus",
    isFrame: "Frame",
  };

  const nameOf = (candidate) => {
    const names = Object.keys(FLAGS).filter((flag) => candidate?.[flag]);
    return names.length ? [...new Set(names.map((flag) => FLAGS[flag]))].join("+") : "unknown";
  };

  // Wallets that share window.ethereum expose the full list under .providers
  const injected = provider.providers?.length ? provider.providers : [provider];
  return injected.map(nameOf);
};

// EIP-6963 replaced the window.ethereum free-for-all. Wallets answer this
// event, so it is the reliable way to see everything installed.
const discoverAnnouncedWallets = () =>
  new Promise((resolve) => {
    const found = new Set();
    const onAnnounce = (event) => found.add(event.detail?.info?.name || "unknown");

    window.addEventListener("eip6963:announceProvider", onAnnounce);
    window.dispatchEvent(new Event("eip6963:requestProvider"));

    setTimeout(() => {
      window.removeEventListener("eip6963:announceProvider", onAnnounce);
      resolve([...found]);
    }, 300);
  });

// Every call here is silent: none of them opens a MetaMask popup.
export const collectWalletDiagnostics = async () => {
  const provider = window.ethereum;
  const expectedChain = getChain()[NETWORK];

  const diagnostics = {
    network: NETWORK,
    expectedChainId: expectedChain?.chainId,
    hasWindowEthereum: Boolean(provider),
    isMetaMask: Boolean(provider?.isMetaMask),
    injectedWallets: detectInjectedWallets(),
    announcedWallets: [],
    currentChainId: null,
    authorisedAccounts: null,
    userAgent: navigator.userAgent,
  };

  try {
    diagnostics.announcedWallets = await discoverAnnouncedWallets();
  } catch (err) {
    diagnostics.announcedWallets = `unavailable: ${err.message}`;
  }

  if (!provider) return diagnostics;

  try {
    diagnostics.currentChainId = await provider.request({ method: "eth_chainId" });
  } catch (err) {
    diagnostics.currentChainId = `failed: ${err.message}`;
  }

  try {
    // eth_accounts never prompts; it reports what is already authorised.
    const accounts = await provider.request({ method: "eth_accounts" });
    diagnostics.authorisedAccounts = accounts.length;
  } catch (err) {
    diagnostics.authorisedAccounts = `failed: ${err.message}`;
  }

  return diagnostics;
};

/**
 * Shows the real reason a connection failed and logs everything needed to
 * diagnose it. `stage` names the step that threw, so console output says
 * which half of the flow broke.
 */
export const reportWalletFailure = async (stage, error) => {
  const described = describeWalletError(error);
  const diagnostics = await collectWalletDiagnostics();

  console.group(`[wallet-connect] failed while ${stage}`);
  console.error("error", error);
  console.table({
    stage,
    code: described.code,
    message: described.raw,
  });
  console.table(diagnostics);
  console.info("Copy the two tables above and send them to the team.");
  console.groupEnd();

  enqueueSnackbar(`${described.message}\n${described.hint}\n(code: ${described.code})`, {
    variant: "error",
    autoHideDuration: 12000,
    style: { whiteSpace: "pre-line" },
    anchorOrigin: {
      horizontal: "right",
      vertical: "top",
    },
  });

  return described;
};
